import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { StepFooterConfig } from './types';

type StepFooterContextValue = {
  footer: StepFooterConfig;
  setFooter: (config: StepFooterConfig) => void;
};

const StepFooterContext = createContext<StepFooterContextValue | null>(null);

export const StepFooterProvider = ({ children }: { children: ReactNode }) => {
  const [footer, setFooter] = useState<StepFooterConfig>(null);
  const value = useMemo(() => ({ footer, setFooter }), [footer, setFooter]);

  return (
    <StepFooterContext.Provider value={value}>
      {children}
    </StepFooterContext.Provider>
  );
};

export const useStepFooterContext = () => {
  const context = useContext(StepFooterContext);
  if (!context) {
    throw new Error(
      'useStepFooterContext must be used within StepFooterProvider'
    );
  }
  return context;
};

/** Registers footer actions with AuthLayout. Clears on unmount. */
export const useStepFooter = (config: NonNullable<StepFooterConfig>) => {
  const { setFooter } = useStepFooterContext();
  const configRef = useRef(config);
  configRef.current = config;

  const hasBackClick = Boolean(config.onBackClick);

  useEffect(() => {
    setFooter({
      continueLabel: config.continueLabel,
      backLabel: config.backLabel,
      isContinueDisabled: config.isContinueDisabled,
      isLoading: config.isLoading,
      onContinueClick: () => configRef.current.onContinueClick(),
      onBackClick: hasBackClick
        ? () => configRef.current.onBackClick?.()
        : undefined
    });

    return () => setFooter(null);
  }, [
    setFooter,
    config.continueLabel,
    config.backLabel,
    config.isContinueDisabled,
    config.isLoading,
    hasBackClick
  ]);
};
